'use client'

import { useState, useEffect } from 'react'
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

const ICON_UPDATE_KEY = 'eisenhowerish-icon-update-dismissed'

// Extend Navigator interface for iOS standalone mode
interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

export default function IconUpdateNotification() {
  const [showNotification, setShowNotification] = useState(false)
  const [isPWA, setIsPWA] = useState(false)

  useEffect(() => {
    // Check if user has dismissed this notification
    const dismissed = localStorage.getItem(ICON_UPDATE_KEY) === 'true'
    
    // Check if app is running as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (navigator as NavigatorWithStandalone).standalone === true
    
    setIsPWA(isStandalone)
    
    // Only show if:
    // - User hasn't dismissed
    // - App is running as PWA
    // - We're on a new icon version
    if (!dismissed && isStandalone) {
      setShowNotification(true)
    }
  }, [])

  const handleDismiss = () => {
    setShowNotification(false)
    localStorage.setItem(ICON_UPDATE_KEY, 'true')
  }

  const handleRefresh = () => {
    // Force service worker update
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          registration.update()
        }
      })
    }
    
    // Show instructions for manual update
    alert(
      'To see the new app icon:\n\n' +
      '• Remove the app from your home screen\n' +
      '• Visit the website again\n' +
      '• Add it back to your home screen\n\n' +
      'The new icon will appear automatically!'
    )
    
    handleDismiss()
  }

  if (!showNotification || !isPWA) {
    return null
  }

  return (
    <div className="fixed top-4 left-4 right-4 z-50">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-lg shadow-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <ArrowPathIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              New App Icon Available! 🎨
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              We&apos;ve updated the app icon. Remove and re-add to your home screen to see it.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-3 flex space-x-2">
          <button
            onClick={handleRefresh}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded-md transition-colors"
          >
            Update Icon
          </button>
          <button
            onClick={handleDismiss}
            className="flex-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 text-blue-700 dark:text-blue-300 text-sm font-medium py-2 px-3 rounded-md transition-colors"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  )
}
